import axiosV1 from "@/libs/axiosV1";
import { ref, Ref, computed } from "@vue/composition-api/dist/vue-composition-api";
import { parsePhoneNumber } from "libphonenumber-js";
import { RouteInterface } from "@/interfaces/route.interface";
import useChannels from "@/composables/v2.1/useChannels";
import axios from "@/libs/axios";
// @ts-ignore
import i18n from "@/libs/i18n";

// @ts-ignore
const currentRoute: Ref<RouteInterface> = ref({});
const routes = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const templates = ref([
    {
        id: null,
        title: i18n.t("settings.smart-routing.templates.template.none"),
    },
]);
const selectedTemplateIsValid = computed(() => {
    return (
        !currentRoute.value?.afterTemplate ||
        templates.value.find((template) => template.id === currentRoute.value.afterTemplate)
    );
});

export default function useRoutes() {
    const { channels } = useChannels();
    const fetchRoutes = () => {
        axiosV1
            .get("routes")
            .then((response) => (routes.value = response.data))
            .finally(() => (isLoading.value = false));
    };

    const fetchTemplatesForChannel = (channelId: number): Promise<any[]> => {
        return axios
            .get("/templates", {
                params: {
                    fields: "title,content",
                    join: "channels||id",
                    s: {
                        $and: [
                            {
                                "channels.id": {
                                    $eq: channelId,
                                },
                            },
                        ],
                    },
                },
            })
            .then((result) => {
                templates.value = [
                    {
                        id: null,
                        title: i18n.t("settings.smart-routing.templates.template.none"),
                    },
                    ...result.data,
                ];
                return result.data;
            });
    };

    const fetchCurrentRoute = (id: number): Promise<any> => {
        isLoading.value = true;
        return axiosV1
            .get(`routes/${id}`)
            .then((response) => {
                const fetchedRoute: RouteInterface = response.data;
                fetchedRoute.identities = fetchedRoute.routeIdentities.map((identity) => identity.identity);
                currentRoute.value = fetchedRoute;
            })
            .finally(() => (isLoading.value = false));
    };

    const saveCurrentRoute = () => {
        isSaving.value = true;

        return axiosV1.post("routes/routes", currentRoute.value).finally(() => (isSaving.value = false));
    };

    const setCurrentRoute = (route: RouteInterface) => {
        currentRoute.value = route;
    };

    const getSettingByName = (settingName, defaultValue = "") => {
        if (!currentRoute.value?.settings) {
            currentRoute.value.settings = [];
        }

        let setting = currentRoute.value.settings.find((setting) => setting.name === settingName);
        if (!setting) {
            setting = {
                name: settingName,
                routeId: currentRoute.value.id,
                value: defaultValue,
            };
            currentRoute.value.settings.push(setting);
        }

        return setting;
    };

    const parseRouteForSave = () => {
        if (!selectedTemplateIsValid.value) {
            throw new Error("Invalid template");
        }

        if (currentRoute.value.identities.length > 0) {
            const channel = channels.value.find((channel) => channel.id === currentRoute.value.channelId);
            if (channel && channel.platform?.toLowerCase() === "whatsapp") {
                const incorrectIdentities = currentRoute.value.identities.find((identity) => {
                    let phoneNumber = undefined;
                    try {
                        phoneNumber = parsePhoneNumber(identity, "NL");
                    } catch {}

                    return !phoneNumber || !phoneNumber.isValid();
                });

                if (incorrectIdentities) {
                    throw new Error("Invalid identity format");
                }
            }
        }

        const contactObject: Record<string, unknown[]> = {};
        currentRoute.value.contactMatchLines.forEach((line) => {
            if (!line.key || !line.value) {
                // Only error if you forgot to fill in something, not for an empty line
                if (line.key || line.value) {
                    throw new Error("Incomplete record");
                }
                return;
            }
            if (contactObject[line.key]) {
                contactObject[line.key].push({ mode: line.compareMode, value: line.value });
            } else {
                contactObject[line.key] = [{ mode: line.compareMode, value: line.value }];
            }
        });
        currentRoute.value.contactDetailsToMatch = JSON.stringify(contactObject);

        currentRoute.value.keywords = currentRoute.value.keywordStrings?.map((keyword) => ({ keyword }));
        currentRoute.value.routeTags = currentRoute.value.tags?.map((tag) => ({
            tagId: tag.id,
        }));
        currentRoute.value.routeLabels = currentRoute.value.labels?.map((label) => ({
            labelId: label.id,
        }));
        currentRoute.value.routeContactLists = currentRoute.value.contactLists?.map((contactList) => ({
            contactListId: contactList.id,
        }));
        currentRoute.value.routeContactListsToRemoves = currentRoute.value.contactListsToRemoves.map((contactList) => ({
            contactListId: contactList.id,
        }));
        currentRoute.value.routeIdentities = currentRoute.value.identities?.map((identity) => ({ identity }));

        return currentRoute.value;
    };

    return {
        routes,
        currentRoute,
        isLoading,
        fetchRoutes,
        getSettingByName,
        fetchCurrentRoute,
        saveCurrentRoute,
        setCurrentRoute,
        parseRouteForSave,
        fetchTemplatesForChannel,
        templates,
        selectedTemplateIsValid,
    };
}
